// heartbeat.ts — keep an open glow page's session alive.
//
// On load (initHeartbeat, called from index.ts at module eval) the SDK fires a
// best-effort GET /_backlit/heartbeat to the glow's edge, then repeats hourly.
// The edge re-mints a near-expiry session cookie and (for signed-in users)
// re-reads the permission, so a single-page glow that only talks to the data
// API stays signed in and picks up permission changes without a navigation.
//
// Best-effort everywhere: every error (including a 401 on an expired/revoked
// session) is swallowed — the heartbeat never throws, never decodes a body,
// and never touches auth.* state. The visible behaviour to the page is nil.
//
// Lifecycle (D4): paused while the tab is hidden (clearInterval on
// document.hidden), fired once on each →visible transition. No pagehide
// teardown. Fire on page load if visible (N1), else wait for the first
// →visible.
//
// Session probe. A beat's HTTP status is the one session signal a page can
// read — a rejected WebSocket upgrade's status is invisible to the browser —
// so the realtime reconnect loop (events.ts) sends beats of its own through
// sendHeartbeat to tell an ended session (401) from a transport drop, and
// watches every beat's outcome through onHeartbeatResult. Beats are
// single-flight: a probe that lands while a lifecycle beat is on the wire joins
// it, so the two never double up. The lifecycle itself still ignores outcomes.
//
// Bounded: a beat's fetch is aborted after 10 s (where AbortController
// exists) and settles as no answer (0), freeing the single-flight slot — so a
// hung request can never wedge a caller waiting on it (the offline drain).
//
// Identity (backlit.offline): a 2xx beat for a verified user session carries
// X-Backlit-UID, and every answer a Date header; both reach the result
// listeners as a HeartbeatInfo second argument (read tolerantly — "" / 0 when
// absent), so the offline layer can prove who the session belongs to.
//
// Version (backlit.checkVersion): a 204 also names the glow's live version in
// X-Backlit-Version ("" = no information: no answer, a non-2xx, or a glow with
// nothing live), so the app-version layer learns of a newer deploy for free.

import { isBacklitHost } from "./host.js";
import { parseServerDate } from "./offline-identity.js";

// HeartbeatInfo is what a settled beat stated: the X-Backlit-UID of a verified
// user session ("" = no information), the server Date in epoch ms (0 = none),
// and the glow's live version from X-Backlit-Version ("" = no information).
export interface HeartbeatInfo {
  uid: string;
  date: number;
  version: string;
}

// DEFAULT_INTERVAL_MS is the hourly cadence. MIN_INTERVAL_MS clamps the
// window.__BACKLIT_HEARTBEAT_MS override so a page can't hammer the edge.
const DEFAULT_INTERVAL_MS = 3_600_000;
const MIN_INTERVAL_MS = 60_000;

const HEARTBEAT_PATH = "/_backlit/heartbeat";

// BEAT_TIMEOUT_MS is when a beat still on the wire is aborted (status 0).
const BEAT_TIMEOUT_MS = 10_000;

// Module state. started gates double-init (index.ts evaluates once, but a
// double-<script src> would re-run it). timer holds the live interval handle
// (null when paused/stopped). onVisibility holds the registered listener so
// _resetHeartbeatForTest can remove it. inflight is the beat currently on the
// wire (single-flight); resultListeners are told every settled beat's status.
let started = false;
let timer: ReturnType<typeof setInterval> | null = null;
let onVisibility: (() => void) | null = null;
let inflight: Promise<number> | null = null;
// abortTimer is the in-flight beat's abort timer; timeoutMs its delay.
let abortTimer: ReturnType<typeof setTimeout> | null = null;
let timeoutMs = BEAT_TIMEOUT_MS;
// lastUid is the X-Backlit-UID the latest settled beat stated ("" = none).
let lastUid = "";
// lastInfo is the latest settled beat's whole HeartbeatInfo.
let lastInfo: HeartbeatInfo = { uid: "", date: 0, version: "" };
const resultListeners = new Set<(status: number, info: HeartbeatInfo) => void>();

// headerOf reads a response header tolerantly (a test double may have none).
function headerOf(res: Response | undefined, name: string): string {
  try {
    return res?.headers?.get?.(name) ?? "";
  } catch {
    return "";
  }
}

// resolveIntervalMs reads the optional window.__BACKLIT_HEARTBEAT_MS override.
// Only a finite number >= MIN_INTERVAL_MS is honoured; anything else (absent,
// NaN, Infinity, non-number, below the floor) falls back to the hourly default.
function resolveIntervalMs(): number {
  const raw = (window as { __BACKLIT_HEARTBEAT_MS?: unknown }).__BACKLIT_HEARTBEAT_MS;
  if (typeof raw === "number" && Number.isFinite(raw) && raw >= MIN_INTERVAL_MS) {
    return raw;
  }
  return DEFAULT_INTERVAL_MS;
}

// sendHeartbeat fires one beat and resolves its HTTP status, or 0 when there is
// no answer: a network error, no answer within 10 s (aborted), or a
// non-browser / off-host context, where nothing is sent. It never rejects, never reads the body, and never inspects
// auth state. A call while a beat is in flight joins that beat.
export function sendHeartbeat(): Promise<number> {
  if (inflight) return inflight;
  if (typeof window === "undefined" || typeof document === "undefined" || !isBacklitHost()) {
    return Promise.resolve(0);
  }
  const none = (): [number, HeartbeatInfo] => [0, { uid: "", date: 0, version: "" }];
  const ac = typeof AbortController === "function" ? new AbortController() : null;
  let t: ReturnType<typeof setTimeout> | null = null;
  let answer: Promise<[number, HeartbeatInfo]>;
  try {
    answer = fetch(HEARTBEAT_PATH, { method: "GET", credentials: "same-origin", ...(ac ? { signal: ac.signal } : {}) }).then(
      (res): [number, HeartbeatInfo] => [
        typeof res?.status === "number" ? res.status : 0,
        {
          uid: headerOf(res, "X-Backlit-UID"),
          date: parseServerDate(headerOf(res, "Date")),
          version: headerOf(res, "X-Backlit-Version"),
        },
      ],
      none,
    );
    if (ac) {
      // The abort settles the beat itself, even if the fetch ignores the signal.
      const timedOut = new Promise<[number, HeartbeatInfo]>((resolve) => {
        t = abortTimer = setTimeout(() => {
          ac.abort();
          resolve(none());
        }, timeoutMs);
        (t as { unref?: () => void }).unref?.();
      });
      answer = Promise.race([answer, timedOut]);
    }
  } catch {
    answer = Promise.resolve(none()); // fetch itself is unavailable or threw synchronously
  }
  const beat: Promise<number> = answer.then(([status, info]) => {
    if (t !== null) {
      clearTimeout(t);
      if (abortTimer === t) abortTimer = null;
    }
    if (inflight === beat) inflight = null;
    lastUid = info.uid;
    lastInfo = info;
    for (const listener of Array.from(resultListeners)) {
      try {
        listener(status, info);
      } catch {
        /* a listener must never break the beat or its siblings */
      }
    }
    return status;
  });
  inflight = beat;
  return beat;
}

// sendHeartbeatWithUid is sendHeartbeat plus the X-Backlit-UID that beat
// stated ("" = none) — the offline drain's identity gate. Single-flight like
// sendHeartbeat (a joined beat reports that beat's uid).
export function sendHeartbeatWithUid(): Promise<{ status: number; uid: string }> {
  return sendHeartbeat().then((status) => ({ status, uid: lastUid }));
}

// sendHeartbeatWithInfo is sendHeartbeat plus that beat's whole HeartbeatInfo
// (the app-version check reads its version). Single-flight like sendHeartbeat.
export function sendHeartbeatWithInfo(): Promise<{ status: number; info: HeartbeatInfo }> {
  return sendHeartbeat().then((status) => ({ status, info: lastInfo }));
}

// onHeartbeatResult registers a listener told every settled beat's status —
// lifecycle beats and sendHeartbeat probes alike (0 = no answer) — plus the
// beat's HeartbeatInfo. Returns an unregister function.
export function onHeartbeatResult(listener: (status: number, info: HeartbeatInfo) => void): () => void {
  resultListeners.add(listener);
  return () => {
    resultListeners.delete(listener);
  };
}

// fireHeartbeat sends the lifecycle's best-effort beat, ignoring the outcome —
// a 401 is a normal, swallowed result.
function fireHeartbeat(): void {
  void sendHeartbeat();
}

// initHeartbeat is the auto-start entry point, called once from index.ts.
//
// Guard order is deliberate (the host check fails OPEN in a non-browser
// harness, so the window/document existence check MUST come first):
//   1. already started → no-op (idempotent against double-load)
//   2. non-browser (no window/document) → no-op
//   3. off a deployed glow host → no-op
export function initHeartbeat(): void {
  if (started) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!isBacklitHost()) return;

  started = true;
  const intervalMs = resolveIntervalMs();

  const startInterval = () => {
    if (timer !== null) return; // already running
    timer = setInterval(fireHeartbeat, intervalMs);
  };
  const stopInterval = () => {
    if (timer === null) return;
    clearInterval(timer);
    timer = null;
  };

  onVisibility = () => {
    if (document.hidden) {
      stopInterval(); // paused while hidden
    } else {
      fireHeartbeat(); // fire once on →visible, then resume
      startInterval();
    }
  };
  document.addEventListener("visibilitychange", onVisibility);

  // Fire on page load only if visible (N1). If hidden at load, the
  // visibilitychange listener handles the first beat on the first →visible.
  if (!document.hidden) {
    fireHeartbeat();
    startInterval();
  }
}

// _resetHeartbeatForTest clears the module-level state so each test starts
// clean — mirrors fab.ts's _resetFABForTest precedent.
export function _resetHeartbeatForTest(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  if (onVisibility !== null && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibility);
  }
  onVisibility = null;
  started = false;
  inflight = null;
  lastUid = "";
  lastInfo = { uid: "", date: 0, version: "" };
  resultListeners.clear();
  if (abortTimer !== null) clearTimeout(abortTimer);
  abortTimer = null;
  timeoutMs = BEAT_TIMEOUT_MS;
}

// _setHeartbeatTimeoutForTest replaces the beat's abort delay (null: 10 s).
export function _setHeartbeatTimeoutForTest(ms: number | null): void {
  timeoutMs = ms ?? BEAT_TIMEOUT_MS;
}
